import { coerceFieldValue } from './coerceFieldValue';
import type {
  ActiveFormDescriptorField,
  ApplyPrefillField,
  PrefillResult,
} from './types';

/**
 * Writes one coerced value into the live form. The publisher supplies an
 * implementation bound to `methods.setValue(name, value, { shouldDirty: true,
 * shouldValidate: true })` so applied fields become dirty (lighting the save
 * disc) and validate immediately. Kept as an injected function so this factory
 * stays pure and unit-testable without react-hook-form.
 */
export type SetFieldValue = (name: string, value: unknown) => void;

/**
 * Build the `applyPrefill` function for a form from its descriptor and a value
 * setter. Each proposed field is validated against the descriptor (the allowlist
 * — a key not on the form is rejected `not_on_form`) then coerced; only fields
 * that pass are written. Never persists — the user saves through the normal
 * form path. Returns the applied/rejected partition so the UI can report exactly
 * what took.
 */
export const createApplyPrefill =
  (descriptor: ActiveFormDescriptorField[], setFieldValue: SetFieldValue) =>
  (fields: ApplyPrefillField[]): PrefillResult => {
    const byKey = new Map(descriptor.map((field) => [field.key, field]));
    const applied: string[] = [];
    const rejected: PrefillResult['rejected'] = [];

    for (const { key, value } of fields) {
      const field = byKey.get(key);
      if (!field) {
        rejected.push({ key, reason: 'not_on_form' });
        continue;
      }

      const result = coerceFieldValue(field, value);
      if (!result.ok) {
        rejected.push({ key, reason: result.reason });
        continue;
      }

      setFieldValue(key, result.value);
      applied.push(key);
    }

    return { applied, rejected };
  };
